import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useOffres from "../../hooks/offre/useOffres";
import Loader from "../components/loader";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { FiSearch } from "react-icons/fi";

function OffresList() {
  const { offres, setOffres, updateOffre, fetchOffres } = useOffres();

  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const offresPerPage = 10;

  // ================= FETCH OFFRES =================

  useEffect(() => {
    const loadOffres = async () => {
      try {
        setLoading(true);
        await fetchOffres();
      } catch (error) {
        console.error("Erreur chargement offres :", error);
      } finally {
        setLoading(false);
      }
    };

    loadOffres();
  }, []);

  // ================= NAVIGATION =================

  const navigateToCreateOffre = () => {
    navigate("/create-offre");
  };

  // ================= ACTIVATE / DEACTIVATE =================

  const handleToggleActiveOffre = (offre) => {
    const newStatus = offre.active === 1 ? 2 : 1;
    const action = newStatus === 1 ? "activer" : "désactiver";

    confirmAlert({
      title: "Confirmation",
      message: `Êtes-vous sûr de vouloir ${action} cette offre ?`,
      buttons: [
        {
          label: "Oui",
          onClick: () => {
            updateOffre(offre._id, { active: newStatus }, () => {
              setOffres((prevOffres) =>
                prevOffres.map((o) =>
                  o._id === offre._id ? { ...o, active: newStatus } : o,
                ),
              );

              setMessage({
                type: "success",
                text: `Offre ${action} avec succès.`,
              });

              setTimeout(() => setMessage(null), 2000);
            });
          },
        },
        { label: "Non" },
      ],
    });
  };

  // ================= FILTER OFFRES =================

  const filteredOffres = offres.filter((offre) =>
    offre.designation?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ================= PAGINATION =================

  const totalPages = Math.ceil(filteredOffres.length / offresPerPage);
  const indexOfLastOffre = currentPage * offresPerPage;
  const indexOfFirstOffre = indexOfLastOffre - offresPerPage;
  const currentOffres = filteredOffres.slice(
    indexOfFirstOffre,
    indexOfLastOffre,
  );

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Offres</h2>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Liste des offres</h3>
        </div>

        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* SEARCH */}
                <div className="position-relative" style={{ width: "300px" }}>
                  <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-secondary" />
                  <input
                    type="text"
                    className="form-control ps-4 rounded-pill"
                    placeholder="Rechercher offre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: "35px" }}
                  />
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Désignation</th>
                      <th>Prix/Mois (€)</th>
                      <th>Prix/Année (€)</th>
                      <th>Liste accès</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentOffres.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="text-center text-secondary py-4"
                        >
                          Aucune offre trouvée
                        </td>
                      </tr>
                    ) : (
                      currentOffres.map((offre) => (
                        <tr key={offre._id}>
                          <td>{offre.designation}</td>
                          <td>{offre.prixMois}</td>
                          <td>{offre.prixAnnee}</td>

                          <td>
                            {Array.isArray(offre.access) &&
                            offre.access.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {offre.access.map((ac, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-info text-dark"
                                    style={{ fontSize: "0.78em" }}
                                  >
                                    {ac}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">Aucun</span>
                            )}
                          </td>

                          <td>
                            {offre.active === 1 ? (
                              <span className="badge bg-success">Activée</span>
                            ) : (
                              <span className="badge bg-secondary">
                                Désactivée
                              </span>
                            )}
                          </td>

                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() =>
                                  navigate(`/edit-offre/${offre._id}`)
                                }
                              >
                                Modifier
                              </button>

                              <button
                                className={`btn btn-sm ${
                                  offre.active === 1
                                    ? "btn-warning"
                                    : "btn-success"
                                }`}
                                onClick={() => handleToggleActiveOffre(offre)}
                              >
                                {offre.active === 1 ? "Désactiver" : "Activer"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <span className="small text-secondary">
                  Page {currentPage} sur {totalPages || 1}
                </span>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant
                </button>
              </div>

              {/* CREATE OFFRE */}
              <div className="text-center mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={navigateToCreateOffre}
                >
                  Créer une Offre
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OffresList;
